'use client';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useProtectedQuery } from '@/hooks/use-protected-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import { Loader } from '@/components/ui/shadcn-io/ai/loader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Check, Globe, Lock, Settings, Timer, Users } from 'lucide-react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { CommandAgent } from '@/types/command-agent';
import { VoiceSlider } from '@/components/shared/voice-slider';
import { CopyButton } from '@/components/shared/copy-button';
import { patch, fetchData } from '@/utils/api-handler';
import { toast } from 'sonner';
import { FormField } from '@/components/shared/form-fields';
import { AppList } from '@/components/shared/app-list';
import { ConnectedAppsActions } from '@/components/shared/connected-apps-actions';
import { DataSourcesList } from '@/components/shared/datasources-list';
import { DataTableIntegrationSelector } from '@/components/data-table-integrations/DataTableIntegrationSelector';
import { ConnectedDataTableIntegrations } from '@/components/data-table-integrations/ConnectedDataTableIntegrations';
import {
    mergeDataTableIntegrationsSelection,
    normalizeDataTableIntegrationsForCompare,
    sanitizeDataTableIntegrations,
} from '@/components/data-table-integrations/constants';
import { UnsavedChangesAlert } from '@/components/shared/unsaved-changes-alert';
import { Switch } from '@/components/ui/switch';
import { MultiSelect } from '@/components/ui/multi-select';
import { isSystemCommandAgent, sameRoleIdLists } from '@/components/command-team/command-agent/utils/command-agent-sharing-utils';
import { applyLlmProviderChange, getDefaultModelForProvider } from '@/components/command-team/command-agent/utils/command-agent-llm-utils';

interface RolesListResponse {
    items?: Array<{ id?: string; title?: string }>;
}

interface LLMModel {
    provider: string;
    model_key: string;
    model_id: string;
    display_name: string;
    context_window: number;
    default_max_tokens: number;
    max_tokens_min: number;
    max_tokens_max: number;
    default_temperature: number;
    temperature_min: number;
    temperature_max: number;
    supports_streaming: boolean;
    supports_tools: boolean;
}

interface LLMModelsResponse {
    models: LLMModel[];
    total: number;
}

const toNumberString = (value: number | null | undefined, defaultValue: string): string => {
    return value !== null && value !== undefined ? value.toString() : defaultValue;
};


function showSettingsUpdatedToast() {
    toast.success('Settings updated successfully');
}

interface AgentSettingsSheetProps {
    agent: CommandAgent | undefined;
    isLoadingAgent: boolean;
    disabled?: boolean;
    agentId?: string;
    /** When provided, sheet is controlled and no trigger is rendered (for use inside dropdowns) */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

type QuickSection = 'agent' | 'apps' | 'model' | 'knowledge' | 'data-tables';

export function AgentSettingsSheet({
    agent,
    isLoadingAgent,
    disabled = false,
    agentId,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
}: AgentSettingsSheetProps) {
    const queryClient = useQueryClient();
    const isSystemAgent = agent ? isSystemCommandAgent(agent) : false;
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        llm_provider: "",
        llm_model: "",
        llm_timeout: "",
        llm_temperature: "",
        llm_max_tokens: "",
        app_integrations: [] as string[],
        custom_apps: [] as string[],
        datasources: [] as string[],
        data_table_integrations: [] as DataTableIntegration[],
        visibility: "PERSONAL" as AppIntegrationVisibility,
        shared_roles: [] as string[],
    });
    const [originalFormData, setOriginalFormData] = useState<any>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [previousModel, setPreviousModel] = useState<string>("");
    const [activeQuickSection, setActiveQuickSection] = useState<QuickSection>('agent');
    const [focusedDataTableId, setFocusedDataTableId] = useState<string | null>(null);
    const agentSectionRef = useRef<HTMLDivElement>(null);
    const appsSectionRef = useRef<HTMLDivElement>(null);
    const modelSectionRef = useRef<HTMLDivElement>(null);
    const knowledgeSectionRef = useRef<HTMLDivElement>(null);
    const dataTablesSectionRef = useRef<HTMLDivElement>(null);
    const formScrollRef = useRef<HTMLDivElement>(null);
    const quickNavRef = useRef<HTMLDivElement>(null);

    const { data: llmModelsData, isLoading: isLoadingModels } = useQuery<LLMModelsResponse>({
        queryKey: ["llm-models"],
        queryFn: () => fetchData("/api/command-team/llm-config/models"),
    });

    const agentConnectionIds = useMemo(() => {
        if (!agent) return { app_integrations: [] as string[], custom_apps: [] as string[] };
        return {
            app_integrations: (agent.app_integrations ?? []).map((app) => app.integration_id),
            custom_apps: (agent.custom_apps ?? []).map((app) => app.custom_app_id),
        };
    }, [agent]);

    const savedDataTableIds = useMemo(() => {
        if (!agent?.data_table_integrations?.length) return [];
        return agent.data_table_integrations.map(
            (integration) => integration.data_table_id
        );
    }, [agent?.data_table_integrations]);

    const { data: rolesQueryData, isLoading: rolesLoading, hasPermission: canListRoles } =
        useProtectedQuery<RolesListResponse | Array<{ id?: string; title?: string }>>({
            queryKey: ["command-agent-role-options-sheet", agentId],
            requiredPermissions: ["LIST_ROLES"],
            queryFn: async () => {
                const params = new URLSearchParams({ page: "1", limit: "500" });
                return fetchData(`/api/rbac/roles?${params.toString()}`);
            },
            enabled: Boolean(agentId),
        });

    const roleOptions = useMemo(() => {
        const raw = rolesQueryData;
        const items = Array.isArray(raw) ? raw : raw?.items ?? [];
        return items
            .filter((r): r is { id: string; title?: string } => Boolean(r.id))
            .map((r) => ({ label: r.title?.trim() || r.id, value: r.id }));
    }, [rolesQueryData]);

    const selectedModel = useMemo(() => {
        if (!formData.llm_model || !llmModelsData?.models) return null;
        return llmModelsData.models.find(
            (model) => model.model_key === formData.llm_model || model.model_id === formData.llm_model
        );
    }, [formData.llm_model, llmModelsData?.models]);

    const filteredModels = useMemo(() => {
        if (!llmModelsData?.models || !formData.llm_provider) return [];
        return llmModelsData.models.filter((model) => model.provider === formData.llm_provider);
    }, [llmModelsData?.models, formData.llm_provider]);

    useEffect(() => {
        // Preserve local edits while queries refresh in background.
        if (hasUnsavedChanges) return;
        if (!agent || !llmModelsData?.models) return;

        const agentModel = llmModelsData.models.find(
            (model) => model.model_id === agent.llm_model || model.model_key === agent.llm_model
        );
        const provider = agent.llm_provider || agentModel?.provider || "";
        const normalizedModelKey = agentModel?.model_key || agent.llm_model || "";

        const initialData = {
            name: agent.name || "",
            description: agent.description || "",
            role: agent.role || "",
            persona: agent.persona || "",
            restrictions: agent.restrictions || "",
            llm_provider: provider,
            llm_model: normalizedModelKey,
            llm_timeout: toNumberString(agent.llm_timeout, "10"),
            llm_temperature: toNumberString(
                agent.llm_temperature,
                agentModel?.default_temperature?.toString() || "0"
            ),
            llm_max_tokens: toNumberString(
                agent.llm_max_tokens,
                agentModel?.default_max_tokens?.toString() || ""
            ),
            app_integrations: agentConnectionIds.app_integrations,
            custom_apps: agentConnectionIds.custom_apps,
            datasources: Array.isArray(agent.datasources)
                ? agent.datasources.map((ds: any) => typeof ds === "string" ? ds : ds.id || ds).filter(Boolean)
                : [],
            data_table_integrations: sanitizeDataTableIntegrations(
                agent.data_table_integrations
            ),
            visibility: agent.visibility ?? "PERSONAL",
            shared_roles: [...(agent.shared_roles ?? [])],
        };

        setFormData(initialData);
        setOriginalFormData({
            ...initialData,
            datasources: [...initialData.datasources],
            app_integrations: [...initialData.app_integrations],
            custom_apps: [...initialData.custom_apps],
            data_table_integrations: [...initialData.data_table_integrations],
            shared_roles: [...initialData.shared_roles],
        });
        setPreviousModel(normalizedModelKey);
        setHasUnsavedChanges(false);
    }, [agent, llmModelsData, agentConnectionIds, hasUnsavedChanges]);

    // Handle model changes: update max_tokens and temperature when model changes (same as settings-tab)
    useEffect(() => {
        if (!formData.llm_model || !selectedModel) return;

        if (formData.llm_model !== previousModel) {
            setFormData((prev) => ({
                ...prev,
                llm_max_tokens: selectedModel.default_max_tokens.toString(),
                llm_temperature: selectedModel.default_temperature.toString(),
            }));
            setPreviousModel(formData.llm_model);
            return;
        }

        if (!formData.llm_max_tokens) {
            setFormData((prev) => ({
                ...prev,
                llm_max_tokens: selectedModel.default_max_tokens.toString(),
            }));
        }

        if (!formData.llm_temperature) {
            setFormData((prev) => ({
                ...prev,
                llm_temperature: selectedModel.default_temperature.toString(),
            }));
        }
    }, [formData.llm_model, formData.llm_max_tokens, formData.llm_temperature, previousModel, selectedModel]);

    useEffect(() => {
        if (!originalFormData) return;
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        setHasUnsavedChanges(hasChanges);
    }, [formData, originalFormData]);

    const updateMutation = useMutation({
        mutationFn: (data: any) => {
            if (!agentId) throw new Error('Agent ID is required');
            return patch(`/api/command-team/agents/${agentId}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-agent", agentId] });
            queryClient.invalidateQueries({ queryKey: ["command-agent-header", agentId] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            setOriginalFormData({
                ...formData,
                datasources: [...formData.datasources],
                app_integrations: [...formData.app_integrations],
                custom_apps: [...formData.custom_apps],
                data_table_integrations: [...formData.data_table_integrations],
                shared_roles: [...formData.shared_roles],
            });
            setHasUnsavedChanges(false);
            setFocusedDataTableId(null);
            showSettingsUpdatedToast();
        },
        onError: (error: any) => {
            toast.error("Failed to update settings", {
                description: error.response?.data?.message || "An unexpected error occurred",
            });
        },
    });

    const handleInputChange = (field: string, value: string | number | string[] | boolean) => {
        setFormData((prev) => ({
            ...prev,
            [field]:
                typeof value === "boolean"
                    ? value
                    : Array.isArray(value)
                      ? value
                      : value.toString(),
        }));
    };

    const handleProviderChange = (provider: string) => {
        setFormData((prev) => applyLlmProviderChange(prev, provider, llmModelsData?.models));
        setPreviousModel(getDefaultModelForProvider(llmModelsData?.models, provider)?.model_key ?? "");
        setHasUnsavedChanges(true);
    };

    const handleIntegrationIdsChange = (integrationIds: string[]) => {
        setFormData((prev) => ({
            ...prev,
            app_integrations: integrationIds,
        }));
    };

    const handleCustomAppIdsChange = (customAppIds: string[]) => {
        setFormData((prev) => ({
            ...prev,
            custom_apps: customAppIds,
        }));
    };

    const handleDataTableIntegrationsChange = (
        integrations: DataTableIntegration[]
    ) => {
        setFormData((prev) => ({
            ...prev,
            data_table_integrations: integrations,
        }));
    };

    const handleDataTableIntegrationsSelect = (selectedIds: string[]) => {
        setFormData((prev) => {
            const previousIds = new Set(
                prev.data_table_integrations.map(
                    (integration) => integration.data_table_id
                )
            );
            const newlyAddedId =
                selectedIds.find((id) => !previousIds.has(id)) ?? null;

            if (newlyAddedId) {
                setFocusedDataTableId(newlyAddedId);
            }

            return {
                ...prev,
                data_table_integrations: mergeDataTableIntegrationsSelection(
                    selectedIds,
                    prev.data_table_integrations,
                    agent?.data_table_integrations
                ),
            };
        });
    };

    const handleSave = () => {
        if (!agentId || !agent) return;

        const changedFields: Record<string, any> = {};

        if (formData.name !== (agent?.name || "")) {
            changedFields.name = formData.name || null;
        }
        if (formData.description !== (agent?.description || "")) {
            changedFields.description = formData.description || null;
        }
        if (formData.role !== (agent?.role || "")) {
            changedFields.role = formData.role || null;
        }
        if (formData.persona !== (agent?.persona || "")) {
            changedFields.persona = formData.persona || null;
        }
        if (formData.restrictions !== (agent?.restrictions || "")) {
            changedFields.restrictions = formData.restrictions || null;
        }
        
        if (formData.llm_provider && formData.llm_provider !== (agent?.llm_provider || "")) {
            changedFields.llm_provider = formData.llm_provider;
            const modelToSend = formData.llm_model || (filteredModels.length > 0 ? filteredModels[0].model_key : null);
            if (modelToSend) {
                changedFields.llm_model = modelToSend;
            }
        }
        if (formData.llm_model && formData.llm_model !== (agent?.llm_model || "") && !changedFields.llm_model) {
            changedFields.llm_model = formData.llm_model;
        }

        if (formData.llm_model !== (agent?.llm_model || "") && selectedModel) {
            changedFields.llm_max_tokens = selectedModel.default_max_tokens;
            if (selectedModel.temperature_min === selectedModel.temperature_max) {
                changedFields.llm_temperature = selectedModel.default_temperature;
            }
        }
        if (formData.llm_timeout !== toNumberString(agent?.llm_timeout, "10")) {
            changedFields.llm_timeout = formData.llm_timeout ? parseInt(formData.llm_timeout) : null;
        }
        if (formData.llm_temperature !== toNumberString(agent?.llm_temperature, "0")) {
            changedFields.llm_temperature = formData.llm_temperature ? parseFloat(formData.llm_temperature) : null;
        }
        if (formData.llm_max_tokens !== toNumberString(agent?.llm_max_tokens, "")) {
            changedFields.llm_max_tokens = formData.llm_max_tokens ? parseInt(formData.llm_max_tokens) : null;
        }

        const baselineIds = {
            app_integrations: (agent.app_integrations ?? []).map((app) => app.integration_id),
            custom_apps: (agent.custom_apps ?? []).map((app) => app.custom_app_id),
        };
        const currentAppIds = [...formData.app_integrations].sort();
        const agentAppIds = [...baselineIds.app_integrations].sort();
        if (JSON.stringify(currentAppIds) !== JSON.stringify(agentAppIds)) {
            changedFields.app_integrations = formData.app_integrations;
        }

        const currentCustomIds = [...formData.custom_apps].sort();
        const agentCustomIds = [...baselineIds.custom_apps].sort();
        if (JSON.stringify(currentCustomIds) !== JSON.stringify(agentCustomIds)) {
            changedFields.custom_apps = formData.custom_apps;
        }

        const currentDataSourceIds = [...formData.datasources].sort();
        const agentDataSourceIds = (Array.isArray(agent?.datasources)
            ? agent.datasources.map((ds: any) => typeof ds === "string" ? ds : ds.id || ds).filter(Boolean)
            : []).sort();
        if (JSON.stringify(currentDataSourceIds) !== JSON.stringify(agentDataSourceIds)) {
            changedFields.datasources = formData.datasources;
        }

        if (
            normalizeDataTableIntegrationsForCompare(formData.data_table_integrations) !==
            normalizeDataTableIntegrationsForCompare(agent.data_table_integrations ?? [])
        ) {
            changedFields.data_table_integrations = sanitizeDataTableIntegrations(
                formData.data_table_integrations
            );
        }

        if (!isSystemAgent) {
            const agentVisibility = agent?.visibility ?? "PERSONAL";
            const agentShared = agent?.shared_roles ?? [];
            const formShared = formData.visibility === "TEAM" ? formData.shared_roles : [];
            if (formData.visibility !== agentVisibility) {
                changedFields.visibility = formData.visibility;
            }
            if (!sameRoleIdLists(formShared, agentShared)) {
                changedFields.shared_roles = formShared;
            }
        }

        if (Object.keys(changedFields).length === 0) {
            toast.info("No changes to save");
            return;
        }

        updateMutation.mutate(changedFields);
    };

    const handleUndo = () => {
        if (originalFormData) {
            setFormData({ ...originalFormData });
            setPreviousModel(originalFormData.llm_model || "");
            setHasUnsavedChanges(false);
            setFocusedDataTableId(null);
        }
    };

    const scrollToSection = (section: QuickSection, ref: React.RefObject<HTMLDivElement | null>) => {
        setActiveQuickSection(section);
        const container = formScrollRef.current;
        const target = ref.current;
        if (!container || !target) return;

        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const quickNavHeight = quickNavRef.current?.offsetHeight ?? 0;
        const topOffset = quickNavHeight + 16;
        const nextTop = container.scrollTop + (targetRect.top - containerRect.top) - topOffset;

        container.scrollTo({
            top: Math.max(0, nextTop),
            behavior: 'smooth',
        });
    };

    const updateActiveSection = useCallback(() => {
        const container = formScrollRef.current;
        if (!container) return;

            const isNearBottom =
                container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
            if (isNearBottom) {
                setActiveQuickSection((prev) => (prev === 'data-tables' ? prev : 'data-tables'));
                return;
            }

            const quickNavHeight = quickNavRef.current?.offsetHeight ?? 0;
            const probeY = container.scrollTop + quickNavHeight + 28;
            const containerRect = container.getBoundingClientRect();

            const sections: Array<{ key: QuickSection; top: number }> = [
                { key: 'agent', el: agentSectionRef.current },
                { key: 'apps', el: appsSectionRef.current },
                { key: 'model', el: modelSectionRef.current },
                { key: 'knowledge', el: knowledgeSectionRef.current },
                { key: 'data-tables', el: dataTablesSectionRef.current },
            ]
                .filter((section): section is { key: QuickSection; el: HTMLDivElement } => Boolean(section.el))
                .map((section) => {
                    // Section top in the scroll container coordinate space.
                    const top =
                        section.el.getBoundingClientRect().top -
                        containerRect.top +
                        container.scrollTop;
                    return { key: section.key, top };
                })
                .sort((a, b) => a.top - b.top);

            if (sections.length === 0) return;

            const eligible = sections.filter((s) => s.top <= probeY + 4);
            const nextActive = (eligible.length > 0 ? eligible[eligible.length - 1] : sections[0]).key;
            setActiveQuickSection((prev) => (prev === nextActive ? prev : nextActive));
    }, []);

    const resetSheetNavigation = useCallback(() => {
        setActiveQuickSection('agent');
        formScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined;

    const handleSheetOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                resetSheetNavigation();
            } else {
                requestAnimationFrame(() => resetSheetNavigation());
            }
            if (isControlled) {
                controlledOnOpenChange?.(open);
            }
        },
        [isControlled, controlledOnOpenChange, resetSheetNavigation],
    );

    useEffect(() => {
        updateActiveSection();
        window.addEventListener('resize', updateActiveSection);
        return () => {
            window.removeEventListener('resize', updateActiveSection);
        };
    }, [updateActiveSection, agent, controlledOpen, hasUnsavedChanges]);

    useEffect(() => {
        if (!isControlled || !controlledOpen) return;
        requestAnimationFrame(() => resetSheetNavigation());
    }, [agent?.id, isControlled, controlledOpen, resetSheetNavigation]);

    const temperatureValue = formData.llm_temperature ? parseFloat(formData.llm_temperature) : (selectedModel?.default_temperature ?? 0.0);
    const temperatureMin = selectedModel?.temperature_min ?? 0.0;
    const temperatureMax = selectedModel?.temperature_max ?? 2.0;
    const isTemperatureFixed = selectedModel && selectedModel.temperature_min === selectedModel.temperature_max;

    return (
        <Sheet
            {...(isControlled
                ? { open: controlledOpen, onOpenChange: handleSheetOpenChange }
                : { onOpenChange: handleSheetOpenChange })}
        >
            {!isControlled && (
                <SheetTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 ml-2 w-8"
                        disabled={disabled || isLoadingAgent}
                    >
                        <Settings className="h-4 w-4" />
                    </Button>
                </SheetTrigger>
            )}
            <SheetContent 
                side="right" 
                className="w-full max-w-[100vw] sm:w-[50vw] sm:max-w-[50vw] min-w-0 overflow-hidden p-0 flex flex-col [&>button]:z-30 [&>button]:top-4 [&>button]:right-4"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b bg-background sticky top-0 z-20">
                    <SheetTitle className="text-xl font-semibold pr-8">Agent Settings</SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground mt-1.5">
                        Configure your command agent settings and behavior
                    </SheetDescription>
                    {(agentId || agent?.id) ? (
                        <Badge
                            variant="outline"
                            className="mt-2.5 flex max-w-full items-center gap-1 border-border/60 py-0.5 pl-2 pr-0.5 font-normal"
                        >
                            <span
                                className="truncate font-mono text-[11px] text-muted-foreground sm:max-w-[280px]"
                                title={agentId || agent?.id}
                            >
                                {agentId || agent?.id}
                            </span>
                            <CopyButton
                                text={agentId || agent?.id || ""}
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                successMessage="Agent ID copied to clipboard"
                            />
                        </Badge>
                    ) : null}
                </SheetHeader>
                {isLoadingAgent ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader size={24} />
                    </div>
                ) : agent ? (
                    <>
                        <div ref={formScrollRef} className="flex-1 overflow-y-auto" onScroll={updateActiveSection}>
                            <div ref={quickNavRef} className="sticky top-0 z-10 border-b border-border/50 bg-background/95 px-4 backdrop-blur-sm sm:px-6">
                                <nav
                                    className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                    aria-label="Agent settings sections"
                                >
                                    {(
                                        [
                                            { id: "agent" as const, label: "Agent", sectionRef: agentSectionRef },
                                            { id: "apps" as const, label: "Apps", sectionRef: appsSectionRef },
                                            { id: "model" as const, label: "Model", sectionRef: modelSectionRef },
                                            { id: "knowledge" as const, label: "Knowledge Base", sectionRef: knowledgeSectionRef },
                                            { id: "data-tables" as const, label: "Data Tables", sectionRef: dataTablesSectionRef },
                                        ]
                                    ).map((item) => {
                                        const isActive = activeQuickSection === item.id;
                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => scrollToSection(item.id, item.sectionRef)}
                                                aria-current={isActive ? "true" : undefined}
                                                className={cn(
                                                    "-mb-px shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
                                                    "outline-none focus-visible:text-primary",
                                                    isActive
                                                        ? "border-primary text-primary"
                                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                                )}
                                            >
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>
                            <UnsavedChangesAlert
                                hasUnsavedChanges={hasUnsavedChanges}
                                isSaving={updateMutation.isPending}
                                onUndo={handleUndo}
                                onSave={() => handleSave()}
                                className="px-6"
                            />
                            <div className="px-6 py-6 pb-24 space-y-8">
                                <div className="space-y-5">
                                    <div ref={agentSectionRef}>
                                        <h3 className="text-sm font-semibold mb-4 text-foreground">General</h3>
                                        <div className="space-y-5">
                                            <FormField
                                                label="Agent Name"
                                                description="Enter a unique name for your command agent"
                                            >
                                                <Input
                                                    id="name"
                                                    value={formData.name}
                                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                                    placeholder="e.g., Support Agent"
                                                    className="border-border/40 focus:border-black h-9"
                                                />
                                            </FormField>

                                            <FormField
                                                label="Role"
                                                description="Specify the role of this agent"
                                            >
                                                <Input
                                                    id="role"
                                                    value={formData.role}
                                                    onChange={(e) => handleInputChange("role", e.target.value)}
                                                    placeholder="e.g., Customer Support Specialist"
                                                    className="border-border/40 focus:border-black h-9"
                                                />
                                            </FormField>

                                            <FormField
                                                label="Description"
                                                description="Brief description of the agent's purpose"
                                            >
                                                <Textarea
                                                    id="description"
                                                    value={formData.description}
                                                    onChange={(e) => handleInputChange("description", e.target.value)}
                                                    placeholder="Command agent for customer support and document generation."
                                                    className="min-h-[80px] resize-y border-border/40 focus:border-black field-sizing-content"
                                                />
                                            </FormField>

                                            {!isSystemAgent ? (
                                            <Card className="border-border/40 shadow-sm">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-base font-medium">Visibility & sharing</CardTitle>
                                                    <CardDescription>
                                                        Control who can access this agent across your organisation.
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-4 pb-6">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {[
                                                            { value: "PERSONAL" as const, icon: Lock, label: "Personal", description: "Only you can see this Agent." },
                                                            { value: "TEAM" as const, icon: Globe, label: "Team", description: "Anyone in your organisation can discover and use this agent." },
                                                        ].map(({ value, icon: Icon, label, description }) => {
                                                            const active = formData.visibility === value;
                                                            return (
                                                                <button
                                                                    key={label}
                                                                    type="button"
                                                                    onClick={() => setFormData((prev) => ({
                                                                        ...prev,
                                                                        visibility: value,
                                                                        shared_roles: value === "TEAM" ? prev.shared_roles : [],
                                                                    }))}
                                                                    className={cn(
                                                                        "relative flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200",
                                                                        active
                                                                            ? "border-primary/30 bg-primary/[0.04] shadow-[0_0_0_1px_hsl(var(--primary)/0.2)]"
                                                                            : "border-border/40 bg-background hover:border-border hover:bg-muted/30"
                                                                    )}
                                                                >
                                                                    <div className={cn(
                                                                        "flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors",
                                                                        active ? "bg-primary/10 text-primary" : "bg-muted/60 text-muted-foreground"
                                                                    )}>
                                                                        <Icon className="size-4" />
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <p className={cn("text-sm font-semibold leading-none", active ? "text-foreground" : "text-muted-foreground")}>
                                                                            {label}
                                                                        </p>
                                                                        <p className="mt-1 text-xs text-muted-foreground leading-snug">{description}</p>
                                                                    </div>
                                                                    <div className={cn(
                                                                        "flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-all",
                                                                        active ? "border-primary bg-primary" : "border-border/50"
                                                                    )}>
                                                                        {active && <Check className="size-2 text-primary-foreground" strokeWidth={3} />}
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    {formData.visibility === "TEAM" && canListRoles ? (
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                <Users className="text-muted-foreground size-4" />
                                                                <p className="text-sm font-medium">Restrict to roles</p>
                                                                <span className="bg-muted text-muted-foreground ml-auto rounded-full px-2 py-0.5 text-[11px]">
                                                                    Optional
                                                                </span>
                                                            </div>
                                                            <p className="text-muted-foreground text-xs leading-relaxed">
                                                                Leave empty to allow all org users. Select roles to limit
                                                                access to specific groups.
                                                            </p>
                                                            <MultiSelect
                                                                options={roleOptions}
                                                                value={formData.shared_roles}
                                                                onValueChange={(value) =>
                                                                    handleInputChange("shared_roles", value)
                                                                }
                                                                placeholder={
                                                                    rolesLoading
                                                                        ? "Loading roles…"
                                                                        : roleOptions.length === 0
                                                                          ? "No roles available"
                                                                          : "Select one or more roles"
                                                                }
                                                                disabled={rolesLoading || roleOptions.length === 0}
                                                                className="border-border/40"
                                                            />
                                                        </div>
                                                    ) : null}

                                                    {formData.visibility === "TEAM" && !canListRoles ? (
                                                        <p className="text-muted-foreground rounded-lg border border-border/40 bg-muted/30 px-4 py-3 text-xs leading-relaxed">
                                                            You don&apos;t have permission to list roles. Ask an
                                                            administrator to grant access if you need to restrict this
                                                            agent by role.
                                                        </p>
                                                    ) : null}
                                                </CardContent>
                                            </Card>
                                            ) : null}

                                            <FormField
                                                label="Persona"
                                                description="Define the agent's personality and expertise"
                                            >
                                                <Textarea
                                                    id="persona"
                                                    value={formData.persona}
                                                    onChange={(e) => handleInputChange("persona", e.target.value)}
                                                    placeholder="An expert in handling customer support queries and providing helpful responses."
                                                    className="min-h-[80px] resize-y border-border/40 focus:border-black field-sizing-content"
                                                />
                                            </FormField>

                                            <FormField
                                                label="Restrictions"
                                                description="Define boundaries and limitations for the agent"
                                            >
                                                <Textarea
                                                    id="restrictions"
                                                    value={formData.restrictions}
                                                    onChange={(e) => handleInputChange("restrictions", e.target.value)}
                                                    placeholder="Do not share sensitive information. Only respond within the scope of your role."
                                                    className="min-h-[80px] resize-y border-border/40 focus:border-black field-sizing-content"
                                                />
                                            </FormField>
                                        </div>
                                    </div>
                                </div>

                                <div ref={appsSectionRef} className="border-t pt-6">
                                    <h3 className="text-sm font-semibold mb-5 text-foreground">Apps</h3>
                                    <div className="space-y-5">
                                        <Card className="shadow-sm" data-card="connected-apps">
                                            <CardHeader className="border-b px-6 py-4">
                                                <div className="space-y-1">
                                                    <CardTitle>Connected Apps</CardTitle>
                                                    <CardDescription>
                                                        Configure integrations for this command agent
                                                    </CardDescription>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="p-6 space-y-6">
                                                <FormField
                                                    label="Apps"
                                                    description="Connect third-party apps to enhance this agent with automated actions like sending emails, creating tasks, or managing data across platforms"
                                                >
                                                    <AppList
                                                        variant="integrations"
                                                        defaultIntegrationIds={agentConnectionIds.app_integrations}
                                                        onIntegrationIdsChange={handleIntegrationIdsChange}
                                                        connectedIntegrations={agent?.app_integrations || []}
                                                        className="border-border/40"
                                                    />
                                                </FormField>
                                                <ConnectedAppsActions
                                                    appIntegrations={agent?.app_integrations || ([] as any[])}
                                                    customApps={[]}
                                                />
                                            </CardContent>
                                        </Card>

                                        <Card className="shadow-sm" data-card="custom-apps">
                                            <CardHeader className="border-b px-6 py-4">
                                                <div className="space-y-1">
                                                    <CardTitle>Custom apps</CardTitle>
                                                    <CardDescription>
                                                        Connect MCP or API custom apps for this command agent
                                                    </CardDescription>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="p-6 space-y-6">
                                                <FormField
                                                    label="Custom apps"
                                                    description="Select custom apps this command agent can use alongside standard integrations"
                                                >
                                                    <AppList
                                                        variant="custom_apps"
                                                        defaultCustomAppIds={agentConnectionIds.custom_apps}
                                                        onCustomAppIdsChange={handleCustomAppIdsChange}
                                                        connectedCustomApps={agent?.custom_apps || []}
                                                        className="border-border/40"
                                                    />
                                                </FormField>
                                                <ConnectedAppsActions
                                                    appIntegrations={[]}
                                                    customApps={(agent as any)?.custom_apps || []}
                                                />
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>

                                <div ref={modelSectionRef} className="border-t pt-6">
                                    <h3 className="text-sm font-semibold mb-5 text-foreground">AI Configuration</h3>
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <FormField
                                                label="LLM Provider"
                                                description="Select the AI provider"
                                            >
                                                <Select
                                                    value={formData.llm_provider || "OPENAI"}
                                                    onValueChange={handleProviderChange}
                                                    key={`provider-${formData.llm_provider}`}
                                                >
                                                    <SelectTrigger className="border-border/40 h-9">
                                                        <SelectValue placeholder="Select a provider" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="OPENAI">OpenAI</SelectItem>
                                                        <SelectItem value="ANTHROPIC">Anthropic</SelectItem>
                                                        <SelectItem value="MOONSHOTAI">MoonshotAI</SelectItem>
                                                        <SelectItem value="ZHIPU">Zhipu</SelectItem>
                                                        <SelectItem value="DEEPSEEK">DeepSeek</SelectItem>
                                                        <SelectItem value="META">Meta</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormField>

                                            <FormField
                                                label="LLM Model"
                                                description="Select the AI model"
                                            >
                                                <Select
                                                    value={formData.llm_model || undefined}
                                                    onValueChange={(value) => handleInputChange("llm_model", value)}
                                                    key={`model-${formData.llm_provider}-${formData.llm_model || "empty"}`}
                                                    disabled={isLoadingModels}
                                                >
                                                    <SelectTrigger className="border-border/40 h-9">
                                                        <SelectValue placeholder={isLoadingModels ? "Loading models..." : "Select a model"} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {filteredModels.map((model) => (
                                                            <SelectItem key={model.model_id} value={model.model_key}>
                                                                {model.display_name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormField>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <Timer className="h-5 w-5 text-muted-foreground" />
                                                <div>
                                                    <div className="font-semibold">Temperature</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {selectedModel
                                                            ? (isTemperatureFixed
                                                                ? "This model has a fixed temperature"
                                                                : `Controls randomness (${temperatureMin} = deterministic, ${temperatureMax} = very random)`)
                                                            : "Controls randomness in responses (0.0-2.0)"
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            {selectedModel ? (
                                                <VoiceSlider
                                                    value={[temperatureValue]}
                                                    onChange={(value) => handleInputChange("llm_temperature", value[0])}
                                                    min={temperatureMin}
                                                    max={temperatureMax}
                                                    step={0.1}
                                                    unit=""
                                                    heading=""
                                                    subheading=""
                                                    showValue={true}
                                                />
                                            ) : (
                                                <VoiceSlider
                                                    value={[formData.llm_temperature ? parseFloat(formData.llm_temperature) : 0]}
                                                    onChange={(value) => handleInputChange("llm_temperature", value[0])}
                                                    min={0}
                                                    max={2}
                                                    step={0.1}
                                                    unit=""
                                                    heading=""
                                                    subheading=""
                                                    showValue={true}
                                                />
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <Timer className="h-5 w-5 text-muted-foreground" />
                                                <div>
                                                    <div className="font-semibold">Timeout</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        Maximum time to wait for AI response (10-300 seconds)
                                                    </div>
                                                </div>
                                            </div>
                                            <VoiceSlider
                                                value={[formData.llm_timeout ? parseInt(formData.llm_timeout) : 10]}
                                                onChange={(value) => handleInputChange("llm_timeout", value[0])}
                                                min={10}
                                                max={300}
                                                step={1}
                                                unit="sec"
                                                heading=""
                                                subheading=""
                                                showValue={true}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <Timer className="h-5 w-5 text-muted-foreground" />
                                                <div>
                                                    <div className="font-semibold">Max Tokens</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {selectedModel
                                                            ? `Maximum tokens in the response (${selectedModel.max_tokens_min}-${selectedModel.max_tokens_max})`
                                                            : "Maximum tokens in the response (select a model to see limits)"
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            {selectedModel ? (
                                                <VoiceSlider
                                                    value={[formData.llm_max_tokens ? parseInt(formData.llm_max_tokens) : selectedModel.default_max_tokens]}
                                                    onChange={(value) => handleInputChange("llm_max_tokens", value[0])}
                                                    min={selectedModel.max_tokens_min}
                                                    max={selectedModel.max_tokens_max}
                                                    step={1}
                                                    unit=""
                                                    heading=""
                                                    subheading=""
                                                    showValue={true}
                                                />
                                            ) : (
                                                <div className="p-3 bg-muted rounded-xl">
                                                    <p className="text-sm text-muted-foreground text-center">
                                                        Please select a model to configure max tokens
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div ref={knowledgeSectionRef} className="border-t pt-6">
                                    <h3 className="text-sm font-semibold mb-5 text-foreground">Knowledge Base</h3>
                                    <div className="space-y-4">
                                        <FormField
                                            label="Knowledge Bases"
                                            description="Select knowledge bases that contain relevant information for this command agent. These will help provide context and answers during interactions."
                                        >
                                            <DataSourcesList
                                                key={`datasources-list-${agentId || "new"}`}
                                                onDataSourcesSelect={(dataSourceIds) => {
                                                    handleInputChange("datasources", dataSourceIds);
                                                }}
                                                defaultValue={formData.datasources}
                                                connectedIds={originalFormData?.datasources ?? []}
                                                className="border-border/40"
                                            />
                                        </FormField>
                                    </div>
                                </div>

                                <div ref={dataTablesSectionRef} className="border-t pt-6">
                                    <h3 className="text-sm font-semibold mb-5 text-foreground">Data Tables</h3>
                                    <div className="space-y-4">
                                        <FormField
                                            label="Data Tables"
                                            description="Connect data tables that the agent can read from and write to during conversations."
                                        >
                                            <DataTableIntegrationSelector
                                                defaultValue={formData.data_table_integrations.map(
                                                    (integration) => integration.data_table_id
                                                )}
                                                onIntegrationsSelect={handleDataTableIntegrationsSelect}
                                                disabled={updateMutation.isPending}
                                                className="border-border/40"
                                            />
                                        </FormField>
                                        <ConnectedDataTableIntegrations
                                            integrations={formData.data_table_integrations}
                                            savedIntegrationIds={savedDataTableIds}
                                            disabled={updateMutation.isPending}
                                            onChange={handleDataTableIntegrationsChange}
                                            focusedDataTableId={focusedDataTableId}
                                            onFocusComplete={() => setFocusedDataTableId(null)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
            </SheetContent>
        </Sheet>
    );
}

